/*
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import { BEM, T } from "@adapptio/lib-components";
import React, { FC, useCallback, useEffect, useRef, useState } from "react";
import { Icon, ICON_SIZE } from "../Icon";
import { Tooltip } from "../Tooltip";

export enum TABS_STYLE {
	DEFAULT = "default",
	COMPACT = "compact",
	LARGE = "large"
}

/**
 * Tab Item Object
 */
export interface ITabItem {
	/** Tab ID */
	id: string;
	/** Tab icon */
	icon?: string;
	/** Label (translated) */
	label?: string;
	/** Tooltip (translated) */
	tooltip?: string;
}

interface ITabItemProps {
	item: ITabItem;
	selected: boolean;
	onSelect: (tabId: string) => void;
	onMount: (tabId: string, itemRef: React.MutableRefObject<HTMLButtonElement>) => void;
}

const TabItem: FC<ITabItemProps> = ({ item, selected, onSelect, onMount }) => {
	const elRef = useRef<HTMLButtonElement>();
	const innerElRef = useRef<HTMLSpanElement>();

	const handleSelect = useCallback(() => {
		onSelect(item.id);
	}, [ item, onSelect ]);

	useEffect(() => {
		if (elRef) {
			onMount(item.id, elRef);
		}
	}, [ item.id, elRef ]);

	const baseClass = BEM("edt-tabs", "item", {
		"is-selected": selected
	});

	return (
		<button className={baseClass.toClassName()} onClick={handleSelect} ref={elRef}>
			<span
				className={baseClass.element("item-inner").toClassName()}
				ref={innerElRef}
				title={!item.tooltip ? item.label : ""}
			>
				{item.icon ? <Icon name={item.icon} size={ICON_SIZE.CUSTOM} /> : null}
				{item.label ? (
					<span className={baseClass.element("item-label").toClassName()}>
						<T domain="editor" term={item.label} />
					</span>
				) : null}
			</span>
			{item.tooltip ? (
				<Tooltip for={innerElRef} openDelay={1200}>
					<T domain="editor" term={item.tooltip} />
				</Tooltip>
			) : null}
		</button>
	);
};

/**
 * Component props
 */
export interface ITabsProps {
	items: ITabItem[];
	selectedId: string;
	style?: TABS_STYLE;
	className?: string;
	onSelect: (tabId: string) => void;
}

/**
 * Tabs component
 */
export const Tabs: FC<ITabsProps> = ({ items, selectedId, style, className, onSelect }) => {
	const [ activeBarCoords, setActiveBarCoords ] = useState<{ left: string; width: string }>(null);

	const containerElRef = useRef<HTMLDivElement>();
	const itemRefs = useRef<{ [K: string]: React.MutableRefObject<HTMLButtonElement> }>({});

	const handleItemMount = useCallback(
		(tabId: string, elRef: React.MutableRefObject<HTMLButtonElement>) => {
			itemRefs.current[tabId] = elRef;
		},
		[ itemRefs ]
	);

	// Calculate active bar position
	useEffect(() => {
		if (!containerElRef.current) {
			return;
		}

		const updateActiveBar = () => {
			const itemRef = itemRefs.current[selectedId]?.current;

			if (!itemRef) {
				return;
			}

			const left = (itemRef.offsetLeft / containerElRef.current.offsetWidth) * 100 + "%";
			const width = (itemRef.offsetWidth / containerElRef.current.offsetWidth) * 100 + "%";

			setActiveBarCoords({
				left: left,
				width: width
			});
		};

		const observer = new ResizeObserver(updateActiveBar);
		observer.observe(containerElRef.current);

		updateActiveBar();

		return () => {
			observer.disconnect();
		};
	}, [ selectedId, containerElRef, items.length ]);

	const baseClass = BEM("edt-tabs", null, {
		style: style || TABS_STYLE.DEFAULT
	});

	if (className) {
		baseClass.add(className);
	}

	return (
		<nav className={baseClass.toClassName()}>
			<div className={baseClass.element("container").toClassName()} ref={containerElRef}>
				{items.map((item) => {
					return (
						<TabItem
							key={item.id}
							item={item}
							selected={item.id === selectedId}
							onSelect={onSelect}
							onMount={handleItemMount}
						/>
					);
				})}
			</div>
			<div
				className={baseClass.element("active-bar").toClassName()}
				style={{
					left: activeBarCoords?.left || 0,
					width: activeBarCoords?.width || 0,
					display: activeBarCoords ? "block" : "none"
				}}
			/>
		</nav>
	);
};
